'use client'

import { chatPrefillMark } from '@/app/lib/guideChat/prefill'
import type { CollectedFieldName } from '@/app/lib/guideChat/types'

import { forwardRef, useImperativeHandle, useEffect, useMemo, useState, useRef, type ReactNode } from 'react'
import { useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@/app/lib/zodResolver'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import {
    createFinancingFormSchemaWithContext,
    FinancingFormData,
    createFinancingFormDefaults,
    BANK_OTHER,
    BANK_UNKNOWN,
    isCustomBankRequired,
    shouldShowBankUnknownOption,
} from '@/app/schemas/loan-comparator/financing.schema'
import { isNewLoan as checkIsNewLoan } from '@/app/constants/loanTypes'
import {
    getLoanSignatureCase,
    getLoanSignatureDateBounds,
    isLoanSignatureDateValid,
} from '@/app/utils/loan/loan-signature-date'
import LoanFormField from '@/app/components/loanComparator/fields/LoanFormField'
import { useLoanPdfImport } from '@/app/contexts/loan-pdf-import-context'
import { BANK_FIELD_KEY, quoteHasLoanInput } from '@/app/utils/loan/loan-pdf-extract-merge'
import AppFormInput from '@/app/components/atomic/AppFormInput'
import AppSelect from '@/app/components/atomic/AppSelect'
import DateField from '@/app/components/healthComparator/steps/DateField'
import { formatDateToDDMMYYYY } from '@/app/utils/helpers'
import { withRequiredAsterisk } from '@/app/utils/required-label'

// Exposed methods interface
export interface FinancingFormHandle {
    validate: () => Promise<boolean>
    getData: () => FinancingFormData
    reset: () => void
    getErrorMessages: () => string[]
}

interface Props {
    className?: string
    layout?: 'legacy' | 'guided'
    betweenSections?: ReactNode
    bankSubLabel?: string
    bankHidden?: boolean
    signatureLocked?: boolean
    onSignatureDateChange?: (date: string, isValid: boolean) => void
    /** Date typed earlier in this wizard session, restored when the form remounts (guided layout only). */
    sessionSignatureDate?: string
}

const RECOLETA = { fontFamily: 'var(--font-recoleta)' }

const LoanComparatorFinancingForm = forwardRef<FinancingFormHandle, Props>(
    (
        {
            className = '',
            layout = 'legacy',
            betweenSections,
            bankSubLabel,
            bankHidden = false,
            signatureLocked = false,
            onSignatureDateChange,
            sessionSignatureDate,
        },
        ref
    ) => {
        // Store state and actions
        const quote = useLoanComparatorStore((state) => state.quote)
        const formOptions = useLoanComparatorStore((state) => state.formOptions)
        const quoteActions = useLoanComparatorStore((state) => state.quoteActions)
        const loanActions = useLoanComparatorStore((state) => state.loan)

        // Local state for showing/hiding fields
        const [showCustomBank, setShowCustomBank] = useState(false)

        // Determine if this is a new loan (Lagarde) or switching (Hamon/Bourquin)
        const isNewLoan = useMemo(() => checkIsNewLoan(quote?.type_quote), [quote?.type_quote])

        // Signature-date bounds + validation per project type (parité legacy Symfony)
        const signatureCase = useMemo(() => getLoanSignatureCase(quote), [quote])
        const signatureBounds = useMemo(() => getLoanSignatureDateBounds(signatureCase), [signatureCase])
        const financingSchema = useMemo(() => createFinancingFormSchemaWithContext(signatureCase), [signatureCase])

        const otherBankSet = useMemo(() => new Set(formOptions?.otherBankList || []), [formOptions?.otherBankList])

        // Get initial bank data from the first loan or quote level
        const initialBankData = useMemo(() => {
            const firstLoan = quote?.loans?.[0]

            let bankName = firstLoan?.bank?.name || quote?.bank_name || ''
            let customBankName = firstLoan?.custom_bank_name || ''
            // Une otherBank est stockée côté serveur comme Autre + custom_bank_name : la re-sélectionner dans la liste
            if (bankName === BANK_OTHER && customBankName && otherBankSet.has(customBankName)) {
                bankName = customBankName
                customBankName = ''
            }
            const storedSignatureDate = formatDateToDDMMYYYY(firstLoan?.new_date_loan || quote?.date_contract_begin || '')
            // Guided step: a server-defaulted or out-of-window date must not show up as already filled
            const signatureDate =
                layout === 'guided'
                    ? sessionSignatureDate ||
                      (quoteHasLoanInput(quote) && isLoanSignatureDateValid(storedSignatureDate, signatureCase)
                          ? storedSignatureDate
                          : '')
                    : storedSignatureDate

            if (bankName || signatureDate) {
                return {
                    bank_name: bankName,
                    custom_bank_name: customBankName,
                    signature_date: signatureDate,
                }
            }
            return createFinancingFormDefaults()
        }, [quote, otherBankSet, layout, sessionSignatureDate, signatureCase])

        const formatErrors = (err: FieldErrors): string[] => {
            return Object.entries(err).flatMap(([key, value]) => {
                if (!value) return []
                if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
                    return [`${key}: ${value.message}`]
                }
                if (typeof value === 'object') {
                    return formatErrors(value as FieldErrors).map((child) => `${key}.${child}`)
                }
                return []
            })
        }

        // Setup React Hook Form
        const {
            register,
            formState: { errors },
            watch,
            trigger,
            getValues,
            reset: resetForm,
            setValue,
        } = useForm<FinancingFormData>({
            resolver: zodResolver(financingSchema),
            mode: 'onBlur',
            reValidateMode: 'onBlur',
            defaultValues: initialBankData,
        })

        // Expose methods to parent
        useImperativeHandle(ref, () => ({
            validate: async () => {
                return await trigger()
            },
            getData: () => {
                const values = getValues()
                // Contrat API inchangé : une otherBank part comme Autre + custom_bank_name
                if (values.bank_name && otherBankSet.has(values.bank_name)) {
                    return { ...values, bank_name: BANK_OTHER, custom_bank_name: values.bank_name }
                }
                return values
            },
            reset: () => {
                resetForm(createFinancingFormDefaults())
            },
            getErrorMessages: () => {
                return formatErrors(errors)
            },
        }))

        // Reset form when SERVER data changes
        const firstLoan = quote?.loans?.[0]
        const serverDataKey = `${quote?.id}-${firstLoan?.bank?.name || ''}`
        const prevServerDataKeyRef = useRef<string | null>(null)

        useEffect(() => {
            if (!quote?.id) return

            const isNewQuote = prevServerDataKeyRef.current !== serverDataKey

            if (isNewQuote) {
                resetForm(initialBankData)
                prevServerDataKeyRef.current = serverDataKey
            }
        }, [serverDataKey, quote?.id, initialBankData, resetForm])

        // Watch bank_name to show/hide custom bank field
        const bankName = watch('bank_name')

        // Update showCustomBank when bank_name changes
        useEffect(() => {
            setShowCustomBank(isCustomBankRequired(bankName))
            if (!isCustomBankRequired(bankName)) {
                setValue('custom_bank_name', '')
            }
        }, [bankName, setValue])

        const { isImported, clearImported } = useLoanPdfImport()

        // Sync form changes to Zustand store
        useEffect(() => {
            const subscription = watch((formData, { name, type }) => {
                // Corrigé à la main, le champ n'est plus « importé » : seule une saisie porte type 'change'
                if (type === 'change' && name === 'bank_name') clearImported(BANK_FIELD_KEY)

                quoteActions.update({
                    bank_name: formData.bank_name
                        ? otherBankSet.has(formData.bank_name)
                            ? BANK_OTHER
                            : formData.bank_name
                        : undefined,
                })

                const currentLoans = useLoanComparatorStore.getState().quote?.loans || []
                if (currentLoans.length > 0 && formData.signature_date) {
                    loanActions.update(currentLoans[0].id, {
                        new_date_loan: formData.signature_date,
                    })
                }
            })
            return () => subscription.unsubscribe()
        }, [watch, quoteActions, loanActions, otherBankSet, clearImported])

        // Build bank options for select (liste unique : banques principales + otherBanks, Autre en dernier)
        const bankOptions = useMemo(() => {
            const options: { label: string; value: string }[] = []

            if (shouldShowBankUnknownOption(isNewLoan)) {
                options.push({ label: BANK_UNKNOWN, value: BANK_UNKNOWN })
            }

            const mainBanks = (formOptions?.bankList || []).filter(
                (bank) => bank !== BANK_OTHER && bank !== BANK_UNKNOWN
            )
            const mainBankSet = new Set(mainBanks)
            const otherBanks = (formOptions?.otherBankList || []).filter(
                (bank) => bank !== BANK_OTHER && bank !== BANK_UNKNOWN && !mainBankSet.has(bank)
            )

            ;[...mainBanks, ...otherBanks].forEach((bank) => {
                options.push({ label: bank, value: bank })
            })

            options.push({ label: BANK_OTHER, value: BANK_OTHER })

            return options
        }, [formOptions?.bankList, formOptions?.otherBankList, isNewLoan])

        const prefilledByChat = useLoanComparatorStore((state) => state.prefilledByChat)
        const chatMark = (name: CollectedFieldName) => chatPrefillMark(prefilledByChat, name)

        // Le prompt d'extraction a sa propre liste de banques : un badge sur une valeur absente du select serait vide
        const isBankImported = useMemo(
            () => isImported(BANK_FIELD_KEY) && bankOptions.some((option) => option.value === bankName),
            [isImported, bankOptions, bankName]
        )

        const signatureDate = watch('signature_date')
        const isSignatureDateValid = useMemo(
            () =>
                /^\d{2}\/\d{2}\/\d{4}$/.test(signatureDate || '') &&
                isLoanSignatureDateValid(signatureDate, signatureCase),
            [signatureDate, signatureCase]
        )

        const [isEditingDate, setIsEditingDate] = useState(false)
        const editedFromRef = useRef('')
        const isDateCollapsed = layout === 'guided' && isSignatureDateValid && !isEditingDate

        useEffect(() => {
            if (!isEditingDate) return
            if (isSignatureDateValid && signatureDate !== editedFromRef.current) setIsEditingDate(false)
        }, [isEditingDate, isSignatureDateValid, signatureDate])

        useEffect(() => {
            onSignatureDateChange?.(signatureDate || '', isSignatureDateValid)
        }, [signatureDate, isSignatureDateValid, onSignatureDateChange])

        const startEditingDate = () => {
            editedFromRef.current = signatureDate || ''
            setIsEditingDate(true)
        }

        const signatureField = (
            <DateField
                id="signature-date"
                name="signature_date"
                register={register}
                setValue={setValue}
                dataTest="signature-date"
                usePicker
                value={signatureDate}
                minDate={signatureBounds.minDate}
                maxDate={signatureBounds.maxDate}
                placeholder={layout === 'guided' ? 'JJ / MM / AAAA' : 'Ex. 01/01/2026'}
            />
        )

        const bankField = !showCustomBank ? (
            <AppSelect
                label=""
                options={bankOptions}
                placeholder="Sélectionner..."
                variant="v2"
                searchable
                {...register('bank_name')}
            />
        ) : (
            <AppFormInput
                label=""
                variant="loan"
                placeholder="Précisez la banque..."
                showClearButton
                onClear={() => setValue('bank_name', '')}
                {...register('custom_bank_name')}
            />
        )

        if (layout === 'guided') {
            return (
                <div className={`loan-comparator-financing-form ${className}`} data-testid="financing-form">
                    {isDateCollapsed ? (
                        <div
                            data-test="signature-date-summary"
                            className="mb-6 flex items-center gap-3 rounded-2xl border border-green-800 bg-green-100 px-4 py-3"
                        >
                            <i className="fa-regular fa-circle-check text-green-800" />
                            <p className="m-0 flex-1 text-[15px] text-green-900">
                                Signature prévue le <strong>{signatureDate}</strong>
                            </p>
                            <button
                                type="button"
                                onClick={startEditingDate}
                                disabled={signatureLocked}
                                title={signatureLocked ? "Modifiable une fois l'analyse terminée" : undefined}
                                data-test="signature-date-edit"
                                className={`text-[14px] font-semibold text-green-900 underline ${
                                    signatureLocked ? 'cursor-not-allowed opacity-50' : ''
                                }`}
                            >
                                Modifier
                            </button>
                        </div>
                    ) : (
                        <div className="mb-6">
                            <h3 style={RECOLETA} className="m-0 mb-3 text-[17px] font-bold text-pale md:text-[19px]">
                                Quand signez-vous votre prêt ?
                            </h3>
                            <div className="grid grid-cols-1 gap-4 rounded-2xl bg-gray-100 p-4 md:grid-cols-2 md:gap-8 md:p-5">
                                <LoanFormField
                                    label="Date prévisionnelle de signature*"
                                    chatPrefill={chatMark('startDate')}
                                    error={errors.signature_date}
                                    htmlFor="signature-date"
                                >
                                    {signatureField}
                                </LoanFormField>
                                <div>
                                    <p className="m-0 text-[14px] font-bold text-pale">
                                        Pourquoi cette date en premier ?
                                    </p>
                                    <p className="m-0 mt-1 text-[14px] text-pale/70">
                                        Elle sert de repère pour interpréter votre offre : elle détermine la date
                                        d&apos;effet de l&apos;assurance et le point de départ des échéances lues dans
                                        le document.
                                    </p>
                                    {isEditingDate && isSignatureDateValid && (
                                        <button
                                            type="button"
                                            onClick={() => setIsEditingDate(false)}
                                            data-test="signature-date-done"
                                            className="mt-2 text-[14px] font-semibold text-rm-blue-accent underline"
                                        >
                                            Terminé
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {betweenSections}

                    <div className={bankHidden ? 'hidden' : 'mb-6'} aria-hidden={bankHidden}>
                        <div className="grid grid-cols-1 items-center gap-3 rounded-2xl border border-rm-gray-light p-4 md:grid-cols-[minmax(0,200px)_minmax(0,1fr)_minmax(0,220px)] md:gap-6 md:p-5">
                            <div>
                                <p style={RECOLETA} className="m-0 text-[15px] font-bold text-pale">
                                    {withRequiredAsterisk(`Banque ${isNewLoan ? 'sollicitée' : 'de votre prêt'}*`)}
                                </p>
                                {bankSubLabel && <p className="m-0 mt-0.5 text-[13px] text-pale/60">{bankSubLabel}</p>}
                            </div>

                            <LoanFormField
                                label=""
                                error={errors.bank_name || errors.custom_bank_name}
                                imported={isBankImported}
                                chatPrefill={chatMark('bank')}
                            >
                                {bankField}
                            </LoanFormField>

                            <p className="m-0 text-[13px] text-pale/60">
                                Vous ne la trouvez pas ? Choisissez « {BANK_OTHER} ».
                            </p>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className={`loan-comparator-financing-form ${className}`} data-testid="financing-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Signature date */}
                    <LoanFormField
                        label={`${isNewLoan ? 'Date prévisionnelle de signature' : 'Signature initiale'} de l'offre de prêt*`}
                        chatPrefill={chatMark('startDate')}
                        tooltip={{
                            title: "Signature initiale de l'offre de prêt",
                            text: isNewLoan
                                ? "Date à laquelle vous allez <strong>signer votre offre de prêt.</strong> Si cette date n'est pas encore définie, indiquez une date approximative. <br><strong>Attention : </strong> il faut commencer à être assuré dès que vous avez signé votre offre de prêt bancaire et non quand vous avez signé l'achat de votre bien."
                                : 'Date à laquelle vous avez <strong>signé votre offre de prêt.</strong>',
                        }}
                        tooltipId="signature-date-tooltip"
                        error={errors.signature_date}
                        htmlFor="signature-date"
                    >
                        {signatureField}
                    </LoanFormField>

                    {/* Bank selection */}
                    <LoanFormField
                        label={`Banque ${isNewLoan ? 'sollicitée' : 'de votre prêt'}*`}
                        tooltip={{
                            title: 'Banque sollicitée',
                            text: isNewLoan
                                ? 'Banque avec laquelle <strong>vous allez contracter votre emprunt.</strong> <u>Si vous hésitez entre plusieurs banques,</u> sélectionnez celle que vous avez le plus de probabilités de choisir : cela nous servira à déterminer les garanties exigées dont vous avez besoin pour que le contrat soit accepté par votre banque. Nous pourrons toujours la modifier après.'
                                : 'Banque avec laquelle <strong>vous avez contracté votre emprunt.</strong> Cela nous servira à déterminer les garanties exigées dont vous avez besoin pour que le contrat soit accepté par votre banque.',
                        }}
                        tooltipId="bank-name-tooltip"
                        error={errors.bank_name || errors.custom_bank_name}
                        imported={isBankImported}
                        chatPrefill={chatMark('bank')}
                    >
                        {bankField}
                    </LoanFormField>
                </div>
            </div>
        )
    }
)

LoanComparatorFinancingForm.displayName = 'LoanComparatorFinancingForm'

export default LoanComparatorFinancingForm
